import { ReactComponent as Menu } from "assets/svg/menu-hamburger.svg";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineDashboard } from "assets/svg/outline-dashboard.svg";
import { ReactComponent as OutlineMessageCircleLines } from "assets/svg/outline-message-circle-lines.svg";
import { useMemo } from "react";
import {
  Dropdown,
  Nav,
  Navbar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { NavLink } from "react-router-dom";
// import { ReactComponent as OutlineBell } from "assets/svg/outline-bell.svg";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import MessageNotifications from "components/message-notifications";
import NavBrand from "components/nav-brand/NavBrand";
import { GENERAL_ROUTES } from "constants/generalRoutes";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useSubscription } from "contexts/subscription-context";
import { useUser } from "contexts/UserContext";
import switchBackToGuardian from "services/patient-chart/switchBackToGuardian";
import switchToMinor from "services/patient-chart/switchToMinor";
import { AmplitudePageName, track } from "utils/amplitude";
import User from "./components/user";
import logoutUser from "./utils/logoutUser";

import getClasses from "utils/getClasses";
import styles from "./AfterLoginNavbar.module.scss";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "common-navigation";

const AfterLoginNavbar = () => {
  const { isPhonePortrait } = usePhonePortraitContext();
  const { canAccessFeature, canAccessPlatform } = useSubscription();
  const { canAccessFeatureByRole } = useTeamContext();
  const { user, updateUserInfo } = useUser();

  const accountsToMap = useMemo(() => {
    return !user
      ? []
      : user.minors
          ?.map(({ minor, orchid_pro }) => ({
            isMinor: true,
            account: minor,
            orchidPro: orchid_pro,
          }))
          .concat([
            {
              isMinor: false,
              account: {
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                orchid_user_id: user.sub,
                preferred_first_name: null,
                billing_user_id: null,
              },
              orchidPro: null,
            },
          ]);
  }, [user]);

  const switchUser = async (isMinor, account, orchidPro) => {
    try {
      if (!isMinor) {
        await switchBackToGuardian();
      } else {
        await switchToMinor(orchidPro.orchid_user_id, account.orchid_user_id);
      }
      await updateUserInfo();
    } catch (error) {
      console.error(error);
    } finally {
      window.location.reload();
    }
  };

  const minor = useMemo(() => {
    return user?.minors?.find(
      ({ minor }) => minor.orchid_user_id === user?.current_minor_user_id,
    )?.minor;
  }, [user]);

  return (
    <Navbar
      className={getClasses(styles["nav-bar"], "shadow-sm background-white")}
      expand="lg"
    >
      <NavBrand />
      <Navbar.Toggle aria-controls="dashboard-navbar-nav" />
      <Navbar.Collapse id="dashboard-navbar-nav">
        <Nav className={!isPhonePortrait ? styles["nav-bar__left"] : ""}>
          {user?.user_type === "orchid_pro" && <>
          <NavLink
            className="nav-link"
            to={GENERAL_ROUTES.professionalSearch}
            onClick={() => {
              if (user?.user_type === "orchid_pro") {
                track(AMPLITUDE_PAGE_NAME, "find-a-professional-link", "click");
              }
            }}
          >
            Find a professional
          </NavLink>
          <NavLink
            className="nav-link"
            to={GENERAL_ROUTES.groupSession}
            onClick={() => {
              if (user?.user_type === "orchid_pro") {
                track(
                  AMPLITUDE_PAGE_NAME,
                  "find-a-support-group-link",
                  "click",
                );
              }
            }}
          >
            Find a support group
          </NavLink>

          <NavLink
            className="nav-link"
            to={GENERAL_ROUTES.pricing}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "pricing-link", "click");
            }}
          >
            Pricing
          </NavLink>
          </>}
          <NavLink
            className="nav-link"
            to={GENERAL_ROUTES.blogs}
            onClick={() => {
              if (user?.user_type === "orchid_pro") {
                track(AMPLITUDE_PAGE_NAME, "blog-link", "click");
              }
            }}
          >
            Blog
          </NavLink>

          {/* <NavLink
            exact
            className="nav-link"
            to={GENERAL_ROUTES.press}
            onClick={() => {
              if (user?.user_type === "orchid_pro") {
                track(AMPLITUDE_PAGE_NAME, "press-link", "click");
              }
            }}
          >
            Press
          </NavLink> */}
        </Nav>
        {user?.user_type === "orchid_pro" ? (
          <Nav
            className={getClasses(
              "ml-auto",
              !isPhonePortrait && "align-items-center",
            )}
          >
            {canAccessPlatform(user) && (
              <>
                {canAccessFeatureByRole("dashboard") && (
                  <NavLink
                    className="nav-link"
                    to="/pro-dashboard/homepage"
                    aria-label={"homepage"}
                    onClick={() => {
                      track(AMPLITUDE_PAGE_NAME, "dashboard-link", "click");
                    }}
                  >
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="button-tooltip">Dashboard</Tooltip>}
                    >
                      <OutlineDashboard aria-hidden="true" />
                    </OverlayTrigger>
                  </NavLink>
                )}

                {canAccessFeature("messaging") &&
                  canAccessFeatureByRole("messaging") && (
                    <NavLink
                      className="nav-link"
                      to="/pro-dashboard/messages"
                      aria-label={"messages"}
                      onClick={() => {
                        track(AMPLITUDE_PAGE_NAME, "messages-link", "click");
                      }}
                    >
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id="button-tooltip">Messages</Tooltip>
                        }
                      >
                        <div aria-hidden="true">
                          <OutlineMessageCircleLines /> <MessageNotifications />
                        </div>
                      </OverlayTrigger>
                    </NavLink>
                  )}
              </>
            )}
            {/* <NavLink
                exact
                className="nav-link"
                to="/pro-dashboard/notifications"
              >
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="button-tooltip">Notifications</Tooltip>}
                >
                  <OutlineBell />
                </OverlayTrigger>
              </NavLink> */}
            <DropdownToggle
              title={<User />}
              className={isPhonePortrait ? "ml-0" : ""}
              Caret={<Menu />}
            >
              {canAccessPlatform(user) && (
                <>
                  <Dropdown.Item
                    href="https://help.orchid.exchange/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      track(AMPLITUDE_PAGE_NAME, "support-link", "click");
                    }}
                  >
                    Support
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={NavLink}
                    to="/pro-dashboard/policies"
                    onClick={() => {
                      track(AMPLITUDE_PAGE_NAME, "policies-link", "click");
                    }}
                  >
                    Policies
                  </Dropdown.Item>
                </>
              )}
              <Dropdown.Item
                onClick={() => {
                  track(AMPLITUDE_PAGE_NAME, "blog-link", "click");
                  logoutUser(user);
                }}
              >
                Log out
              </Dropdown.Item>
            </DropdownToggle>
          </Nav>
        ) : (
          <Nav className="ml-auto align-items-center">
            {!accountsToMap?.length ? null : (
              <div className={styles["minor-dropdown-container"]}>
                View as
                <DropdownToggle
                  className={styles["minor-dropdown"]}
                  title={
                    <div className={styles["minor-dropdown__title"]}>
                      {minor
                        ? `${minor.first_name || ""} ${minor.last_name || ""}`
                        : `${user?.first_name || ""} ${
                            user?.last_name || ""
                          } (You)`}
                    </div>
                  }
                  Caret={<OutlineChevronDown />}
                >
                  {accountsToMap.map((el) => (
                    <Dropdown.Item
                      onClick={() =>
                        switchUser(el.isMinor, el.account, el.orchidPro)
                      }
                    >
                      {el.account.first_name || ""} {el.account.last_name || ""}
                      {!el.isMinor && " (You)"}
                    </Dropdown.Item>
                  ))}
                </DropdownToggle>
              </div>
            )}
            <NavLink
              style={{ whiteSpace: "nowrap" }}
              className="nav-link"
              to="/member-dashboard/messages"
              aria-label={"messages"}
            >
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="button-tooltip">Messages</Tooltip>}
              >
                <div aria-hidden="true">
                  <OutlineMessageCircleLines /> <MessageNotifications />
                </div>
              </OverlayTrigger>
            </NavLink>
            <NavLink
              className="nav-link"
              to="/member-dashboard/homepage"
              aria-label={"homepage"}
            >
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="button-tooltip">Dashboard</Tooltip>}
              >
                <OutlineDashboard aria-hidden="true" />
              </OverlayTrigger>
            </NavLink>
            <DropdownToggle title={<User />} Caret={<Menu />}>
              <Dropdown.Item as={NavLink} to="/member-dashboard/sessions">
                My sessions
              </Dropdown.Item>
              <Dropdown.Item as={NavLink} to="/member-dashboard/assignments">
                Assignment
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item as={NavLink} to="/member-dashboard/edit-profile">
                Profile
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                href="https://help.orchid.exchange/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Help
              </Dropdown.Item>
              <Dropdown.Item
                as={NavLink}
                to={"/member-dashboard/switch-accounts"}
              >
                Switch profile
              </Dropdown.Item>
              <Dropdown.Item onClick={() => logoutUser(user)}>
                Log out
              </Dropdown.Item>
            </DropdownToggle>
          </Nav>
        )}
      </Navbar.Collapse>
    </Navbar>
  );
};

export default AfterLoginNavbar;
